import React, { useState, useEffect, useCallback } from 'react';
import { Link } from 'react-router-dom';
import { Card, CardContent } from './ui/card';
import { Button } from './ui/button';
import { waitlistAPI } from '../services/api';
import { Logo } from './Logo';
import {
  Scissors,
  Clock,
  Users,
  ArrowLeft,
  RefreshCw,
  Loader2,
  AlertCircle,
} from 'lucide-react';

const BARBERS = ['Pete', 'Mckee', 'Chris', 'First Available'];

const formatWait = (minutes) => {
  if (minutes === 0) return 'No wait';
  if (minutes < 60) return `~${minutes} min`;
  const h = Math.floor(minutes / 60);
  const m = minutes % 60;
  return m === 0 ? `~${h} hr` : `~${h} hr ${m} min`;
};

const formatCloseTime = (isoString) => {
  if (!isoString) return null;
  const d = new Date(isoString);
  return d.toLocaleTimeString('en-US', { hour: 'numeric', minute: '2-digit', hour12: true });
};

export const PublicWaitlistPage = () => {
  const [selectedBarber, setSelectedBarber] = useState('Pete');
  const [data, setData] = useState(null);
  const [isLoading, setIsLoading] = useState(true);

  const fetchData = useCallback(async () => {
    try {
      const result = await waitlistAPI.getPublicByBarber(selectedBarber);
      setData(result);
    } catch (err) {
      setData(null);
    } finally {
      setIsLoading(false);
    }
  }, [selectedBarber]);

  useEffect(() => {
    setIsLoading(true);
    fetchData();
    // Auto-refresh every 30 seconds
    const interval = setInterval(fetchData, 30000);
    return () => clearInterval(interval);
  }, [fetchData]);

  const summary = data?.barbers?.[selectedBarber] || { count: 0, estimated_wait_minutes: 0, exceeds_closing: false };
  const queue = data?.queue || [];
  const shop = data?.shop || {};
  const isClosed = !shop.is_open;
  const closesAt = formatCloseTime(shop.closes_at);

  return (
    <div className="min-h-screen bg-stone-50">
      {/* Header */}
      <header className="bg-stone-700 border-b border-stone-600">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 py-5 flex items-center justify-between">
          <Link to="/" className="flex items-center gap-3 group" data-testid="back-to-home">
            <Logo variant="full-light" className="h-12 w-auto" />
            <p className="hidden sm:block text-xs text-stone-400 border-l border-stone-500 pl-3">
              Live Waitlist
            </p>
          </Link>
          <Button
            onClick={fetchData}
            variant="outline"
            size="sm"
            className="border-stone-500 text-stone-100 hover:bg-stone-600 hover:text-stone-100"
            data-testid="refresh-public-btn"
          >
            <RefreshCw className="h-4 w-4" />
          </Button>
        </div>
      </header>

      <main className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 py-10">
        <div className="mb-8">
          <Link
            to="/"
            className="inline-flex items-center gap-2 text-sm text-stone-600 hover:text-maroon-800 transition-colors mb-4"
          >
            <ArrowLeft className="h-4 w-4" />
            Back to homepage
          </Link>
          <h2 className="text-3xl sm:text-4xl font-bold text-stone-900 mb-2">
            Current Waitlist
          </h2>
          <p className="text-stone-600">
            Choose a barber to see the live queue and estimated wait time. Auto-updates every 30 seconds.
          </p>
        </div>

        {/* Shop Closed Banner */}
        {isClosed && (
          <Card className="bg-stone-800 border-stone-700 text-stone-100 mb-6" data-testid="closed-banner">
            <CardContent className="p-5 flex items-start gap-3">
              <AlertCircle className="h-6 w-6 text-amber-400 flex-shrink-0 mt-0.5" />
              <div>
                <p className="font-bold text-lg">
                  {shop.is_closed_today ? "We're closed today" : "We're closed for the day"}
                </p>
                <p className="text-sm text-stone-300 mt-1">
                  {shop.is_closed_today
                    ? "Open Tuesday through Friday, 9:00 AM - 5:30 PM."
                    : "Come back tomorrow! Hours: Tue-Fri 9:00 AM - 5:30 PM."}
                </p>
              </div>
            </CardContent>
          </Card>
        )}

        {/* Closing Soon Warning */}
        {!isClosed && summary.exceeds_closing && (
          <Card className="bg-amber-50 border-amber-300 mb-6" data-testid="closing-warning">
            <CardContent className="p-4 flex items-start gap-3">
              <AlertCircle className="h-5 w-5 text-amber-700 flex-shrink-0 mt-0.5" />
              <p className="text-sm text-amber-900">
                <span className="font-semibold">Heads up:</span> Some people in line may not be served before we close{closesAt ? ` at ${closesAt}` : ''}.
              </p>
            </CardContent>
          </Card>
        )}

        {/* Barber selector */}
        <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 mb-8">
          {BARBERS.map((barber) => {
            const barberData = data?.barbers?.[barber];
            const count = barberData?.count || 0;
            const isSelected = selectedBarber === barber;
            return (
              <button
                key={barber}
                onClick={() => setSelectedBarber(barber)}
                className={`p-4 rounded-xl border-2 text-left transition-all duration-200 ${
                  isSelected
                    ? 'bg-maroon-800 border-maroon-900 text-stone-100'
                    : 'bg-white border-stone-200 text-stone-900 hover:border-maroon-700'
                }`}
                data-testid={`barber-select-${barber.toLowerCase().replace(' ', '-')}`}
              >
                <p className="font-bold text-lg">{barber}</p>
                <p className={`text-sm mt-1 ${isSelected ? 'text-rose-100' : 'text-stone-600'}`}>
                  {isClosed
                    ? `${count} on list`
                    : `${count} ${count === 1 ? 'person' : 'people'} waiting`}
                </p>
              </button>
            );
          })}
        </div>

        {/* Selected barber summary */}
        <Card className="bg-stone-700 border-stone-600 text-stone-100 mb-8">
          <CardContent className="p-8">
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-6 text-center sm:text-left">
              <div>
                <p className="text-sm text-stone-400 uppercase tracking-wide mb-2">Barber</p>
                <p className="text-2xl font-bold flex items-center justify-center sm:justify-start gap-2">
                  <Scissors className="h-6 w-6 text-maroon-400" />
                  {selectedBarber}
                </p>
              </div>
              <div>
                <p className="text-sm text-stone-400 uppercase tracking-wide mb-2">In Line</p>
                <p className="text-2xl font-bold flex items-center justify-center sm:justify-start gap-2" data-testid="line-count">
                  <Users className="h-6 w-6 text-maroon-400" />
                  {summary.count} {summary.count === 1 ? 'person' : 'people'}
                </p>
              </div>
              <div>
                <p className="text-sm text-stone-400 uppercase tracking-wide mb-2">Estimated Wait</p>
                <p className="text-2xl font-bold flex items-center justify-center sm:justify-start gap-2" data-testid="wait-time">
                  <Clock className="h-6 w-6 text-maroon-400" />
                  {isClosed
                    ? 'Closed'
                    : summary.exceeds_closing
                      ? `Until close${closesAt ? ` (${closesAt})` : ''}`
                      : formatWait(summary.estimated_wait_minutes)}
                </p>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Queue list (anonymized) */}
        {isLoading ? (
          <div className="text-center py-12">
            <Loader2 className="h-8 w-8 text-stone-400 animate-spin mx-auto" />
          </div>
        ) : queue.length === 0 ? (
          <Card className="bg-white border-stone-200">
            <CardContent className="p-12 text-center">
              <Users className="h-12 w-12 text-stone-300 mx-auto mb-4" />
              <h3 className="text-xl font-semibold text-stone-900 mb-2">
                No one in line for {selectedBarber}
              </h3>
              <p className="text-stone-600 mb-6">
                You could be next! Come on in or join the waitlist now.
              </p>
              <Link to="/#contact">
                <Button className="bg-maroon-800 hover:bg-maroon-900 text-stone-100">
                  Join Waitlist
                </Button>
              </Link>
            </CardContent>
          </Card>
        ) : (
          <div className="space-y-3">
            <h3 className="text-sm font-semibold text-stone-600 uppercase tracking-wide mb-3">
              Queue Position
            </h3>
            {queue.map((entry) => (
              <Card
                key={entry.position}
                className={`transition-colors ${
                  entry.past_closing
                    ? 'bg-amber-50 border-amber-300'
                    : 'bg-white border-stone-200 hover:border-maroon-700'
                }`}
              >
                <CardContent className="p-5 flex items-center gap-4">
                  <div className={`w-12 h-12 rounded-full flex items-center justify-center flex-shrink-0 ${
                    entry.past_closing ? 'bg-amber-200' : 'bg-stone-100'
                  }`}>
                    <span className="text-stone-900 font-bold">#{entry.position}</span>
                  </div>
                  <div className="flex-1 min-w-0">
                    <p className="font-semibold text-stone-900 break-words">
                      {entry.name || `Customer ${entry.position}`}
                    </p>
                    <p className="text-sm text-stone-600 mt-1">
                      {entry.service} · {entry.party_size} {entry.party_size === 1 ? 'person' : 'people'}
                    </p>
                  </div>
                  <div className="text-right flex-shrink-0">
                    <p className="text-xs text-stone-500 uppercase tracking-wide">
                      {entry.past_closing ? 'Status' : 'Wait'}
                    </p>
                    <p className={`font-semibold ${entry.past_closing ? 'text-amber-800' : 'text-maroon-800'}`}>
                      {entry.past_closing
                        ? (isClosed ? 'Closed' : 'After hours')
                        : formatWait(entry.estimated_wait_minutes)}
                    </p>
                  </div>
                </CardContent>
              </Card>
            ))}

            <div className="pt-6 text-center">
              <Link to="/#contact">
                <Button size="lg" className="bg-maroon-800 hover:bg-maroon-900 text-stone-100 px-8" data-testid="join-waitlist-cta">
                  Add Yourself to the Waitlist
                </Button>
              </Link>
            </div>
          </div>
        )}

        <p className="text-center text-xs text-stone-500 mt-10">
          Wait times are estimates based on services ahead. Actual wait may vary.
        </p>
      </main>
    </div>
  );
};

export default PublicWaitlistPage;
